import { useState } from "react";
import Calendar, { type CalendarProps } from "react-calendar";
import { useIntl } from "react-intl";
import ChevronLeft from "src/design-system/icons/ChevronLeft.svg?react";
import ChevronRight from "src/design-system/icons/ChevronRight.svg?react";
import type {
  LooseValue,
  Value,
} from "react-calendar/dist/esm/shared/types.js";
import messages from "./DatePicker.messages";
import "./calendar.css";
import looseValueToDate from "./looseValueToDate";
import { isLooseDateRange } from "./utils/datePredicates";

type Props = CalendarProps & {
  isSelectingReturn?: boolean;
  isProjectingTripNights?: boolean;
};

export type LooseCalendarDate = LooseValue;
export type CalendarDate = Value;

export function BaseCalendar({ className, ...props }: Props) {
  const intl = useIntl();

  const [activeStartDate, setActiveStartDate] = useState<Date | null>(() => {
    if (props.defaultActiveStartDate) {
      return props.defaultActiveStartDate;
    }

    if (isLooseDateRange(props.value)) {
      return looseValueToDate(props.value[0]);
    } else if (props.value) {
      return looseValueToDate(props.value);
    }

    if (isLooseDateRange(props.defaultValue)) {
      return looseValueToDate(props.defaultValue[0]);
    } else if (props.defaultValue) {
      return looseValueToDate(props.defaultValue);
    }

    return props.minDate ?? null;
  });

  const hasSelectedRange =
    Array.isArray(props.value) && props.value.filter(Boolean).length === 2;

  const calendarClassName = `${className} ${
    props.showDoubleView && "double-view"
  } ${props.isProjectingTripNights && "projecting"} ${
    hasSelectedRange && "range-selected"
  }`;

  return (
    <Calendar
      className={calendarClassName}
      minDetail="month"
      maxDetail="month"
      calendarType="gregory" // week starts on Sunday
      locale={intl.locale}
      nextLabel={<ChevronRight className="h-5 w-5" />}
      prevLabel={<ChevronLeft className="h-5 w-5" />}
      nextAriaLabel={intl.formatMessage(messages.nextMonth)}
      prevAriaLabel={intl.formatMessage(messages.prevMonth)}
      showFixedNumberOfWeeks
      tileClassName={({ date }) => {
        const isLastDayOfMonth =
          date.getDate() ===
          new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
        const isFirstDayOfMonth = date.getDate() === 1;
        const className = `weekday-${date.getDay()}`
          .concat(isLastDayOfMonth ? " last-day" : "")
          .concat(isFirstDayOfMonth ? " first-day" : "")
          .concat(
            isLooseDateRange(props.value) || props.selectRange ? " return" : "",
          );
        return className;
      }}
      activeStartDate={activeStartDate ?? undefined}
      {...props}
      // Ignore the active start date being reset to the range start when a selection is made, OR when selecting next months date in a double view.
      // This is the default behavior, but we want to keep the calendar static unless the user explicitly wants to move forward or backward a month.
      onActiveStartDateChange={({ action, activeStartDate, value, view }) => {
        if (action === "next" || action === "prev") {
          setActiveStartDate(activeStartDate);
          props.onActiveStartDateChange?.({
            action,
            activeStartDate,
            value,
            view,
          });
        }
      }}
    />
  );
}
